import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api/api';

const PlayoffSeries = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeGame, setActiveGame] = useState(0); // index of selected game tab

  useEffect(() => {
    const fetchSeries = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/playoffs/series/${id}`);
        setData(res.data);
        // Default to last played game
        if (res.data.games && res.data.games.length > 0) {
          setActiveGame(res.data.games.length - 1);
        }
      } catch (err) {
        setError('Failed to load series details');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchSeries();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-red-50 dark:from-gray-900 dark:to-gray-800 py-8">
        <div className="container mx-auto px-4 text-center py-20">
          <div className="text-2xl text-gray-600 dark:text-gray-400">Loading series…</div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-red-50 dark:from-gray-900 dark:to-gray-800 py-8">
        <div className="container mx-auto px-4 text-center py-20">
          <div className="text-xl text-red-500">{error || 'Series not found'}</div>
          <Link to="/playoffs" className="mt-4 inline-block text-blue-600 hover:underline">← Back to Bracket</Link>
        </div>
      </div>
    );
  }

  const { series, games, leaders } = data;
  const higher = series.higherSeed;
  const lower  = series.lowerSeed;
  const higherWins = series.higherSeedWins || 0;
  const lowerWins  = series.lowerSeedWins  || 0;
  const higherWon  = series.isComplete && series.winnerId === series.higherSeedId;
  const lowerWon   = series.isComplete && series.winnerId === series.lowerSeedId;

  // ── Box score helpers ────────────────────────────────────────────────────
  const pct = (made, att) => att > 0 ? `${Math.round((made / att) * 100)}%` : '-';
  const shotStr = (made, att) => att > 0 ? `${made}/${att}` : '-';

  const StatTable = ({ stats, teamName }) => {
    const sorted = [...stats].sort((a, b) => b.points - a.points);
    if (!sorted.length) return <p className="text-sm text-gray-400 italic px-2 py-4">No stats recorded</p>;

    const totals = sorted.reduce((acc, s) => ({
      points: acc.points + (s.points || 0),
      rebounds: acc.rebounds + (s.rebounds || 0),
      assists: acc.assists + (s.assists || 0),
      steals: acc.steals + (s.steals || 0),
      blocks: acc.blocks + (s.blocks || 0),
      turnovers: acc.turnovers + (s.turnovers || 0),
      fieldGoalsMade: acc.fieldGoalsMade + (s.fieldGoalsMade || 0),
      fieldGoalsAttempted: acc.fieldGoalsAttempted + (s.fieldGoalsAttempted || 0),
      threePointersMade: acc.threePointersMade + (s.threePointersMade || 0),
      threePointersAttempted: acc.threePointersAttempted + (s.threePointersAttempted || 0),
      freeThrowsMade: acc.freeThrowsMade + (s.freeThrowsMade || 0),
      freeThrowsAttempted: acc.freeThrowsAttempted + (s.freeThrowsAttempted || 0),
    }), {
      points: 0, rebounds: 0, assists: 0, steals: 0, blocks: 0, turnovers: 0,
      fieldGoalsMade: 0, fieldGoalsAttempted: 0,
      threePointersMade: 0, threePointersAttempted: 0,
      freeThrowsMade: 0, freeThrowsAttempted: 0,
    });

    const cols = [
      { key: 'player',  label: 'Player',    cls: 'text-left min-w-[120px]' },
      { key: 'min',     label: 'MIN',       cls: 'text-center w-10' },
      { key: 'pts',     label: 'PTS',       cls: 'text-center w-10 font-semibold' },
      { key: 'reb',     label: 'REB',       cls: 'text-center w-10' },
      { key: 'ast',     label: 'AST',       cls: 'text-center w-10' },
      { key: 'fg',      label: 'FG',        cls: 'text-center w-14' },
      { key: 'fgpct',   label: 'FG%',       cls: 'text-center w-12' },
      { key: '3pt',     label: '3PT',       cls: 'text-center w-14' },
      { key: 'ft',      label: 'FT',        cls: 'text-center w-14' },
      { key: 'stl',     label: 'STL',       cls: 'text-center w-10' },
      { key: 'blk',     label: 'BLK',       cls: 'text-center w-10' },
      { key: 'to',      label: 'TO',        cls: 'text-center w-10' },
    ];

    return (
      <div className="mb-2">
        <div className="text-sm font-bold text-gray-700 dark:text-gray-300 px-1 mb-1 uppercase tracking-wide">{teamName}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-700">
                {cols.map(c => (
                  <th key={c.key} className={`${c.cls} px-1 py-1.5 text-gray-500 dark:text-gray-400 font-semibold`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((s, i) => {
                const name = s.player ? `${s.player.firstName} ${s.player.lastName}` : 'Unknown';
                return (
                  <tr key={s.id || i} className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="text-left px-1 py-1.5 font-medium text-gray-800 dark:text-gray-200 truncate max-w-[140px]">
                      {s.player?.jerseyNumber != null && (
                        <span className="text-gray-400 mr-1">#{s.player.jerseyNumber}</span>
                      )}
                      {name}
                    </td>
                    <td className="text-center px-1 py-1.5 text-gray-500 dark:text-gray-400">{s.minutes ?? '-'}</td>
                    <td className="text-center px-1 py-1.5 font-bold text-gray-900 dark:text-white">{s.points ?? 0}</td>
                    <td className="text-center px-1 py-1.5">{s.rebounds ?? 0}</td>
                    <td className="text-center px-1 py-1.5">{s.assists ?? 0}</td>
                    <td className="text-center px-1 py-1.5 text-gray-600 dark:text-gray-400">{shotStr(s.fieldGoalsMade, s.fieldGoalsAttempted)}</td>
                    <td className="text-center px-1 py-1.5 text-gray-500 dark:text-gray-400">{pct(s.fieldGoalsMade, s.fieldGoalsAttempted)}</td>
                    <td className="text-center px-1 py-1.5 text-gray-600 dark:text-gray-400">{shotStr(s.threePointersMade, s.threePointersAttempted)}</td>
                    <td className="text-center px-1 py-1.5 text-gray-600 dark:text-gray-400">{shotStr(s.freeThrowsMade, s.freeThrowsAttempted)}</td>
                    <td className="text-center px-1 py-1.5">{s.steals ?? 0}</td>
                    <td className="text-center px-1 py-1.5">{s.blocks ?? 0}</td>
                    <td className="text-center px-1 py-1.5">{s.turnovers ?? 0}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 font-semibold">
                <td className="text-left px-1 py-1.5 text-gray-600 dark:text-gray-400 text-xs">TOTAL</td>
                <td className="text-center px-1 py-1.5">-</td>
                <td className="text-center px-1 py-1.5 font-bold">{totals.points}</td>
                <td className="text-center px-1 py-1.5">{totals.rebounds}</td>
                <td className="text-center px-1 py-1.5">{totals.assists}</td>
                <td className="text-center px-1 py-1.5 text-gray-500">{shotStr(totals.fieldGoalsMade, totals.fieldGoalsAttempted)}</td>
                <td className="text-center px-1 py-1.5 text-gray-500">{pct(totals.fieldGoalsMade, totals.fieldGoalsAttempted)}</td>
                <td className="text-center px-1 py-1.5 text-gray-500">{shotStr(totals.threePointersMade, totals.threePointersAttempted)}</td>
                <td className="text-center px-1 py-1.5 text-gray-500">{shotStr(totals.freeThrowsMade, totals.freeThrowsAttempted)}</td>
                <td className="text-center px-1 py-1.5">{totals.steals}</td>
                <td className="text-center px-1 py-1.5">{totals.blocks}</td>
                <td className="text-center px-1 py-1.5">{totals.turnovers}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  // ── Leaders card ─────────────────────────────────────────────────────────
  const LeaderCard = ({ title, emoji, players, statKey, displayFn }) => {
    if (!players || !players.length) return null;
    const top = players[0];
    const topName = top.player ? `${top.player.firstName} ${top.player.lastName}` : 'Unknown';
    const topTeam = top.team?.abbreviation || '';
    const topVal  = displayFn ? displayFn(top) : top[statKey];

    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <div className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">{emoji} {title} Leader</div>
        {/* Top player */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-bold text-gray-900 dark:text-white">{topName}</div>
            <div className="text-xs text-gray-500">{topTeam}</div>
          </div>
          <div className="text-3xl font-black text-blue-600 dark:text-blue-400">{topVal}</div>
        </div>
        {/* Rest */}
        {players.slice(1).map((p, i) => {
          const name = p.player ? `${p.player.firstName} ${p.player.lastName}` : 'Unknown';
          const val  = displayFn ? displayFn(p) : p[statKey];
          return (
            <div key={i} className="flex items-center justify-between py-1 border-t border-gray-100 dark:border-gray-700 text-sm">
              <span className="text-gray-700 dark:text-gray-300">{name} <span className="text-gray-400 text-xs">{p.team?.abbreviation}</span></span>
              <span className="font-semibold text-gray-800 dark:text-gray-200">{val}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Active game data ─────────────────────────────────────────────────────
  const currentGame = games[activeGame];
  const homeStats = currentGame?.stats?.filter(s => s.teamId === currentGame.homeTeamId) || [];
  const awayStats = currentGame?.stats?.filter(s => s.teamId === currentGame.awayTeamId) || [];

  const gameLabel = (game) => {
    const hs = game.homeScore ?? 0;
    const as = game.awayScore ?? 0;
    const home = game.homeTeam?.abbreviation || '?';
    const away = game.awayTeam?.abbreviation || '?';
    return `${home} ${hs} – ${as} ${away}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-red-50 dark:from-gray-900 dark:to-gray-800 py-6">
      <div className="container mx-auto px-4 max-w-5xl">

        {/* Back */}
        <Link to="/playoffs" className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline text-sm mb-5">
          ← Back to Bracket
        </Link>

        {/* Series header */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg dark:shadow-gray-900 p-5 md:p-7 mb-6">
          <div className="text-center text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">
            {series.round} · {series.seriesFormat || 'Single Elimination'}
          </div>

          <div className="flex items-center justify-center gap-4 md:gap-8">
            {/* Higher seed */}
            <div className={`flex-1 text-center ${higherWon ? '' : lowerWon ? 'opacity-50' : ''}`}>
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">{higher?.city}</div>
              <div className="text-xl md:text-2xl font-extrabold text-gray-900 dark:text-white leading-tight">{higher?.name}</div>
              {higherWon && <div className="mt-2 text-xs font-bold text-green-600 bg-green-100 dark:bg-green-900/30 rounded-full px-3 py-0.5 inline-block">✓ WINNER</div>}
            </div>

            {/* Score */}
            <div className="flex flex-col items-center gap-1">
              <div className="flex items-center gap-3">
                <span className={`text-4xl md:text-5xl font-black ${higherWon ? 'text-green-600' : 'text-gray-800 dark:text-gray-200'}`}>{higherWins}</span>
                <span className="text-2xl text-gray-300 dark:text-gray-600">–</span>
                <span className={`text-4xl md:text-5xl font-black ${lowerWon ? 'text-green-600' : 'text-gray-800 dark:text-gray-200'}`}>{lowerWins}</span>
              </div>
              <div className="text-xs text-gray-400">
                {series.isComplete ? '🏆 Series Over' : `${games.length} game${games.length !== 1 ? 's' : ''} played`}
              </div>
            </div>

            {/* Lower seed */}
            <div className={`flex-1 text-center ${lowerWon ? '' : higherWon ? 'opacity-50' : ''}`}>
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">{lower?.city}</div>
              <div className="text-xl md:text-2xl font-extrabold text-gray-900 dark:text-white leading-tight">{lower?.name}</div>
              {lowerWon && <div className="mt-2 text-xs font-bold text-green-600 bg-green-100 dark:bg-green-900/30 rounded-full px-3 py-0.5 inline-block">✓ WINNER</div>}
            </div>
          </div>

          {/* Champion banner */}
          {series.isComplete && series.winner && (
            <div className="mt-5 text-center py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-xl">
              <span className="text-white font-black text-lg">🏆 {series.winner.city} {series.winner.name} advance!</span>
            </div>
          )}
        </div>

        {/* Nothing played yet */}
        {games.length === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-8 text-center text-gray-400">
            No games played yet in this series.
          </div>
        )}

        {games.length > 0 && (
          <>
            {/* Series Leaders */}
            {(leaders.points?.length > 0 || leaders.rebounds?.length > 0 || leaders.assists?.length > 0) && (
              <div className="mb-6">
                <h2 className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-3">Series Leaders</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <LeaderCard title="Points"   emoji="🏀" players={leaders.points}   statKey="points"   />
                  <LeaderCard title="Rebounds" emoji="💪" players={leaders.rebounds} statKey="rebounds" />
                  <LeaderCard title="Assists"  emoji="🎯" players={leaders.assists}  statKey="assists"  />
                  <LeaderCard
                    title="FG%"  emoji="🎳"
                    players={leaders.fgPct}
                    displayFn={p => `${Math.round(p.fgPct * 100)}%`}
                  />
                </div>
              </div>
            )}

            {/* Game tabs */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg dark:shadow-gray-900 overflow-hidden">
              {/* Tab bar */}
              <div className="flex overflow-x-auto border-b border-gray-200 dark:border-gray-700">
                {games.map((game, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveGame(i)}
                    className={`flex-shrink-0 px-4 py-3 text-sm font-semibold transition-colors ${
                      activeGame === i
                        ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                    }`}
                  >
                    Game {game.gameNumber}
                  </button>
                ))}
              </div>

              {/* Active game */}
              {currentGame && (
                <div className="p-4 md:p-6">
                  {/* Game score header */}
                  <div className="flex items-center justify-between mb-5">
                    <div className="text-center flex-1">
                      <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">{currentGame.homeTeam?.abbreviation}</div>
                      <div className="text-2xl font-black text-gray-900 dark:text-white">{currentGame.homeScore ?? 0}</div>
                    </div>
                    <div className="text-center px-4">
                      <div className="text-sm font-bold text-gray-400">Game {currentGame.gameNumber}</div>
                      {currentGame.gameDate && (
                        <div className="text-xs text-gray-400 mt-0.5">
                          {new Date(currentGame.gameDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </div>
                      )}
                    </div>
                    <div className="text-center flex-1">
                      <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">{currentGame.awayTeam?.abbreviation}</div>
                      <div className="text-2xl font-black text-gray-900 dark:text-white">{currentGame.awayScore ?? 0}</div>
                    </div>
                  </div>

                  {/* Box scores */}
                  {(homeStats.length > 0 || awayStats.length > 0) ? (
                    <div className="space-y-5">
                      {homeStats.length > 0 && (
                        <StatTable
                          stats={homeStats}
                          teamName={`${currentGame.homeTeam?.name || 'Home'}`}
                        />
                      )}
                      {awayStats.length > 0 && (
                        <StatTable
                          stats={awayStats}
                          teamName={`${currentGame.awayTeam?.name || 'Away'}`}
                        />
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-400 text-sm italic">
                      No player stats recorded for this game.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Game log summary (all games in series) */}
            {games.length > 1 && (
              <div className="mt-5 bg-white dark:bg-gray-800 rounded-xl shadow p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">All Results</div>
                <div className="space-y-1.5">
                  {games.map((game, i) => (
                    <div
                      key={i}
                      onClick={() => setActiveGame(i)}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition ${
                        activeGame === i
                          ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                      }`}
                    >
                      <span className="text-xs font-semibold text-gray-500 w-14">Game {game.gameNumber}</span>
                      <span className="text-sm font-bold text-gray-800 dark:text-gray-200 flex-1 text-center">{gameLabel(game)}</span>
                      {game.gameDate && (
                        <span className="text-xs text-gray-400 w-16 text-right">
                          {new Date(game.gameDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PlayoffSeries;
